// Purchase requests (spec §25; ADR 0022). Staff ask to buy something, the
// request is approved through Hub's approvals (a frozen snapshot of its
// lines, total and vendor, ADR 0004), and then someone creates the purchase
// order in QuickBooks and types its PO number here.
//
// By the owner's decision, official purchase orders live in QuickBooks. Hub
// keeps no editable purchase order and never calls QuickBooks: the PO
// number and QuickBooks vendor name are typed by hand and never checked.
// Amounts are staff estimates in whole cents, for approval, not accounting.

import { z } from 'zod'

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()

// ------------------------------------------------------------------ words

export const PURCHASE_QBO_NOTE =
  'The PO number is typed from QuickBooks and not checked.'
export const PURCHASE_ESTIMATE_NOTE = 'Estimates, not accounting.'
export const PURCHASE_GRANT_NOTE = 'Not accounting; grant spending is in QuickBooks.'

// ----------------------------------------------------------------- status

export const PURCHASE_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'ordered', 'received', 'closed', 'canceled'] as const
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number]
export const PURCHASE_STATUS_LABELS: Record<PurchaseStatus, string> = {
  draft: 'Draft',
  submitted: 'Waiting for approval',
  approved: 'Approved',
  rejected: 'Rejected',
  ordered: 'Ordered',
  received: 'Received',
  closed: 'Closed',
  canceled: 'Canceled',
}
// Still being worked: everything but closed and canceled.
export const PURCHASE_OPEN_STATUSES: readonly PurchaseStatus[] = ['draft', 'submitted', 'approved', 'rejected', 'ordered', 'received']
// Lines, vendor and the rest can be edited until the PO exists.
export const PURCHASE_EDITABLE_STATUSES: readonly PurchaseStatus[] = ['draft', 'submitted', 'rejected', 'approved']

export const PURCHASE_RECEIPT_KINDS = ['partial', 'complete'] as const
export type PurchaseReceiptKind = (typeof PURCHASE_RECEIPT_KINDS)[number]
export const PURCHASE_RECEIPT_LABELS: Record<PurchaseReceiptKind, string> = { partial: 'Partly received', complete: 'Everything received' }

export const PURCHASE_LINK_TYPES = ['project', 'event', 'facility', 'space', 'asset', 'grant'] as const
export type PurchaseLinkType = (typeof PURCHASE_LINK_TYPES)[number]
export const PURCHASE_LINK_LABELS: Record<PurchaseLinkType, string> = { project: 'Project', event: 'Event', facility: 'Facility', space: 'Space', asset: 'Asset', grant: 'Grant' }

export const purchaseRequestLabel = (number: number) => `PR-${number}`

export const PURCHASE_MAX_LINES = 50

// A line's total and the request's, in whole cents.
export const purchaseLineTotal = (line: { quantity: number; unitCostCents: number }) => line.quantity * line.unitCostCents
export const purchaseTotal = (lines: readonly { quantity: number; unitCostCents: number }[]) => lines.reduce((n, l) => n + purchaseLineTotal(l), 0)

// Whether a request of this total may skip approval: only when a threshold
// is set (more than $0) and the total is under it. The default, $0, means
// every request needs approval.
export const skipsApproval = (totalCents: number, thresholdCents: number) => thresholdCents > 0 && totalCents < thresholdCents

// ------------------------------------------------------------------ shapes

export type PurchaseLine = { id: string; position: number; description: string; quantity: number; unitCostCents: number; totalCents: number }

export type PurchaseLink = { id: string; targetType: PurchaseLinkType; targetId: string; label: string | null }

export type PurchaseQuote = {
  documentId: string
  title: string
  filename: string | null
  version: number | null
  sizeBytes: number | null
  addedByName: string | null
  createdAt: string
  classification: string
}

export type PurchaseReceipt = { id: string; kind: PurchaseReceiptKind; receivedOn: string; note: string | null; recordedByName: string | null; recordedAt: string }

export type PurchaseApproval = {
  approvalId: string
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'invalidated'
  createdAt: string
  // The request's lines, total, vendor, title or justification changed after
  // this was asked for.
  changedSince: boolean
}

export type PurchaseRequestSummary = {
  id: string
  number: number
  label: string
  title: string
  status: PurchaseStatus
  requesterPersonId: string
  requesterName: string | null
  vendorOrganizationId: string | null
  vendorName: string | null
  // A vendor typed by hand that isn't in the vendor register (yet).
  newVendor: boolean
  departmentId: string | null
  departmentName: string | null
  totalCents: number
  currency: 'USD'
  neededBy: string | null
  qboPoNumber: string | null
  createdAt: string
  submittedAt: string | null
  updatedAt: string
}

export type PurchaseRequest = PurchaseRequestSummary & {
  description: string | null
  lines: PurchaseLine[]
  links: PurchaseLink[]
  quotes: PurchaseQuote[]
  receipts: PurchaseReceipt[]
  approval: PurchaseApproval | null
  // Approved with no approval because the total was under this threshold.
  approvalSkippedUnderCents: number | null
  approvedAt: string | null
  qboVendorName: string | null
  orderedOn: string | null
  orderedByName: string | null
  orderNote: string | null
  receivedState: PurchaseReceiptKind | null
  closedAt: string | null
  closedNote: string | null
  canceledAt: string | null
  cancelReason: string | null
  canceledByName: string | null
  // Whether the PO number can be recorded now, and if not, why.
  canOrder: { ok: boolean; reason: string | null }
  version: number
  can: {
    edit: boolean
    submit: boolean
    cancel: boolean
    manage: boolean
    attach: boolean
    proposeVendor: boolean
  }
}

export type PurchasingSettings = { approvalThresholdCents: number; updatedAt: string | null; updatedByName: string | null }

export type PurchaseOptions = {
  departments: { id: string; name: string }[]
  vendors: { organizationId: string; name: string; status: string }[]
  approvers: { personId: string; name: string }[]
  // Only the kinds the viewer may see.
  targets: Partial<Record<PurchaseLinkType, { id: string; label: string }[]>>
  settings: PurchasingSettings
}

// ------------------------------------------------------------------ bodies

export const PurchaseLineInput = z.object({
  description: text(300).min(1),
  quantity: z.number().int().min(1).max(100_000),
  unitCostCents: z.number().int().min(0).max(100_000_000),
})

export const PurchaseLinkInput = z.object({ targetType: z.enum(PURCHASE_LINK_TYPES), targetId: ID })

const PurchaseFields = {
  title: text(200).min(1),
  description: optionalText(4000),
  // One of the two, or neither while it is a draft: a vendor from the
  // register (an organization), or a new vendor's name typed by hand.
  vendorOrganizationId: ID.nullable().optional(),
  newVendorName: optionalText(200),
  departmentId: ID.nullable().optional(),
  neededBy: DAY.nullable().optional(),
  lines: z.array(PurchaseLineInput).max(PURCHASE_MAX_LINES).optional(),
  links: z.array(PurchaseLinkInput).max(30).optional(),
}

export const PurchaseCreateBody = z.object(PurchaseFields)
export const PurchasePatch = z.object({ ...PurchaseFields, title: text(200).min(1).optional(), expectedVersion: z.number().int().min(1) })

export const PurchaseSubmitBody = z.object({
  approverPersonIds: z.array(ID).max(12).optional(),
  rule: z.enum(['all', 'any']).default('all'),
  dueOn: DAY.nullable().optional(),
  note: optionalText(2000),
  // The hash of the snapshot the requester was shown, from the preview.
  expectedSnapshotHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  expectedVersion: z.number().int().min(1),
})

export const PurchaseOrderedBody = z.object({
  qboPoNumber: text(60).min(1),
  qboVendorName: optionalText(200),
  orderedOn: DAY,
  note: optionalText(1000),
  expectedVersion: z.number().int().min(1),
})

export const PurchaseReceivedBody = z.object({ kind: z.enum(PURCHASE_RECEIPT_KINDS), receivedOn: DAY, note: optionalText(1000), expectedVersion: z.number().int().min(1) })
export const PurchaseCloseBody = z.object({ note: optionalText(1000), expectedVersion: z.number().int().min(1) })
export const PurchaseCancelBody = z.object({ reason: text(1000).min(3), expectedVersion: z.number().int().min(1) })
export const PurchaseVendorBody = z
  .object({ organizationId: ID.optional(), create: z.boolean().optional(), expectedVersion: z.number().int().min(1) })
  .refine((b) => !!b.organizationId !== !!b.create, 'Choose an organization or ask to create one')
export const PurchasingSettingsBody = z.object({ approvalThresholdCents: z.number().int().min(0).max(100_000_000) })

export const PURCHASE_VIEWS = ['mine', 'awaiting', 'all'] as const
export type PurchaseView = (typeof PURCHASE_VIEWS)[number]

export const PurchaseListQuery = z.object({
  view: z.enum(PURCHASE_VIEWS).optional(),
  status: z.enum(['open', 'all', ...PURCHASE_STATUSES]).optional(),
  vendor: z.string().max(100).optional(),
  department: z.string().max(100).optional(),
  project: z.string().max(100).optional(),
  event: z.string().max(100).optional(),
  grant: z.string().max(100).optional(),
  facility: z.string().max(100).optional(),
  space: z.string().max(100).optional(),
  asset: z.string().max(100).optional(),
  from: DAY.optional(),
  to: DAY.optional(),
  q: z.string().max(100).optional(),
})
export type PurchaseListParams = z.infer<typeof PurchaseListQuery>
